import React, { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-hot-toast';
import { getUserProductWishlist, getUserCart, addProdToCart, syncLocalCart } from '../features/user/userSlice';
import { base_url } from '../utils/axiosConfig';
import axios from 'axios';
import emptywishlist from '../images/empty_wishlist.png';
import { FaArrowLeft, FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import ProductCard from '../components/ProductCard';
import ProductSkeleton from '../components/ProductSkeleton';

const showToast = (type, message, subtext) => {
  const isSuccess = type === 'success';
  toast.custom((t) => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        background: '#fff',
        border: `1px solid ${isSuccess ? '#d1fae5' : '#fee2e2'}`,
        borderLeft: `4px solid ${isSuccess ? '#10b981' : '#ef4444'}`,
        borderRadius: '12px',
        padding: '12px 16px',
        minWidth: '260px',
        maxWidth: '340px',
        opacity: t.visible ? 1 : 0,
        transform: t.visible ? 'translateY(0)' : 'translateY(-12px)',
        transition: 'all 0.3s ease',
        cursor: 'pointer',
        zIndex: 9999,
      }}
      onClick={() => toast.dismiss(t.id)}
    >
      <div style={{
        width: 36, height: 36, borderRadius: '50%',
        background: isSuccess ? '#d1fae5' : '#fee2e2',
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
      }}>
        {isSuccess
          ? <FaCheckCircle color="#10b981" size={18} />
          : <FaTimesCircle color="#ef4444" size={18} />}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 700, fontSize: '14px', color: '#111', lineHeight: 1.3 }}>{message}</div>
        {subtext && <div style={{ fontSize: '11px', color: '#6b7280', marginTop: 2 }}>{subtext}</div>}
      </div>
    </div>
  ), { duration: 2500 });
};

const Wishlist = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { wishlist, isLoading, isError, message, isAuthenticated, cartProducts, user } = useSelector((state) => state.auth);
  const role = user?.role || JSON.parse(localStorage.getItem('customer'))?.role;
  const [selectedVariants, setSelectedVariants] = useState({});

  // Memoize cartItems to avoid unnecessary re-computations
  const cartItems = useMemo(() => {
    return isAuthenticated ? cartProducts : JSON.parse(localStorage.getItem('cart')) || [];
  }, [isAuthenticated, cartProducts]);

  // Effect 1: Fetch data only on mount or auth change (no cartProducts dep to prevent loops)
  useEffect(() => {
    if (isAuthenticated) {
      dispatch(getUserProductWishlist());
      dispatch(getUserCart());
    }
  }, [dispatch, isAuthenticated]); // Removed cartProducts from deps

  // Effect 2: Sync localStorage cart changes (e.g., after non-auth add/remove)
  useEffect(() => {
    if (!isAuthenticated) {
      // Listen for storage changes (optional: for cross-tab sync, but basic here)
      const handleStorageChange = () => {
        // Trigger re-compute via memo (no explicit setState needed)
      };
      window.addEventListener('storage', handleStorageChange);
      return () => window.removeEventListener('storage', handleStorageChange);
    }
  }, [isAuthenticated]);

  const handleRemoveFromWishlist = async (prodId) => {
    const token = JSON.parse(localStorage.getItem('customer'))?.token;
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    };

    try {
      const response = await axios.put(`${base_url}user/removewishlist`, { prodId }, config);
      if (response.status === 200) {
        showToast('success', 'Removed from Wishlist', 'Item removed successfully.');
        dispatch(getUserProductWishlist());
      } else {
        showToast('error', 'Removal Failed', 'Could not remove item.');
      }
    } catch (error) {
      showToast('error', 'Error', 'Something went wrong.');
    }
  };

  // Removed redundant handlers as ProductCard manages its own cart/quantity/wishlist logic

  return (
    <>
      <div className="wishlist-wrapper py-4 px-5 bg-light" style={{ minHeight: '100vh', overflow: 'auto' }}> {/* Ensure scrollable */}
        <div className="container-xxl">
          <div className="d-flex align-items-center mb-4">
            <div onClick={() => navigate(-1)} style={{ cursor: 'pointer' }} className="back-icon me-3">
              <FaArrowLeft size={20} />
            </div>
            <h2 className="wishlist-title mb-0">Your Wishlist</h2>
          </div>

          {isLoading && (
            <div className="text-center py-4">
              <p>Loading wishlist...</p>
            </div>
          )}

          {isError && (
            <div className="text-center text-danger py-3">
              <p>{message || 'Failed to load wishlist.'}</p>
            </div>
          )}

          {!isLoading && !isError && (
            <div className="row g-3">
              {Array.isArray(wishlist) && wishlist.filter(item => item && item._id)?.length > 0 ? (
                wishlist
                  .filter(item => item && item._id)
                  .map((item, index) => (
                    <ProductCard 
                      key={item._id || index}
                      data={item}
                      grid={3} 
                    />
                  ))
              ) : (
                <div className="text-center w-100 py-5">
                  <div className="mb-3">
                    <img src={emptywishlist} alt="Empty Wishlist" style={{ width: '150px', opacity: 0.6 }} />
                  </div>
                  <h4 className="text-muted">Your Wishlist is Empty</h4>
                  <p className="text-muted mb-4">Add some items to your wishlist to see them here.</p>
                  <Link to="/product" className="btn btn-dark px-4 py-2" style={{ borderRadius: '8px' }}>
                    Continue Shopping
                  </Link>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Wishlist;